import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { Tag } from 'primereact/tag';
import {
    ServiceStatisticsFilter,
    ServiceStatisticsMetricsDTO,
    ServiceStatisticsServiceDTO,
} from '../../dto/ServiceStatisticsDTO';
import { StatisticsAccessProblem } from '../../services/ServiceStatisticsController';
import PeriodDetailCauses from './PeriodDetailCauses';
import PeriodDetailDays from './PeriodDetailDays';
import PeriodDetailHours from './PeriodDetailHours';
import PeriodDetailKpis, { KpiReference } from './PeriodDetailKpis';
import PeriodDetailLongestWaits from './PeriodDetailLongestWaits';
import PeriodDetailServices from './PeriodDetailServices';
import { periodFilter, PeriodTarget, PeriodView, periodRange } from './periodDetail';
import { StatisticsMode } from './statisticsMode';
import { countDays, Measure, parseIsoDate, scopeToService, TimelinePoint } from './statisticsUtils';
import { periodTitle } from './TimelineChart';
import { usePeriodDetail } from './usePeriodDetail';

interface PeriodDetailSidebarProps {
    /** El período que se mira (el punto del gráfico y cómo estaba agrupado); null con el panel cerrado. */
    target: PeriodTarget | null;
    /** Los filtros de lo que se ve, con el rango completo de la pantalla: el panel pone las fechas del período. */
    filter: ServiceStatisticsFilter;
    measure: Measure;
    /** Indicadores de todo el período de la pantalla: contra ellos se compara el período que se mira. */
    overall: ServiceStatisticsMetricsDTO;
    /** Los servicios y agendas de todo el período de la pantalla: de ahí sale lo habitual de cada uno. */
    services: ServiceStatisticsServiceDTO[];
    /** Atenciones de un período promedio: por día (los días con atenciones) y por semana o mes del gráfico. */
    averageAttentions: { perDay: number; perPeriod: number };
    /** Agenda de consultorio elegida, para mostrar su horario del día; 0 si no hay. */
    scheduleAgendaId: number;
    mode?: StatisticsMode;
    /** «Ver solo este día» (o «Ver solo este servicio y este día»): aplica el rango, y el servicio si no es cero, a la pantalla. */
    onViewRange: (from: string, to: string, serviceId: number) => void;
    onHide: () => void;
    /** El backend no deja ver el detalle a este usuario. */
    onAccessProblem: (problem: StatisticsAccessProblem) => void;
}

/** Por dónde se fue entrando en el panel (un día de una semana, un servicio de un período…): sirve para volver. */
interface Navigation {
    owner: PeriodTarget;
    trail: PeriodView[];
}

function SectionLoading() {
    const intl = useIntl();
    return (
        <span className="nv-loading ss-detail__loading" role="status">
            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
            <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
        </span>
    );
}

interface SectionProps {
    id: string;
    title: string;
    children: ReactNode;
}

function Section({ id, title, children }: SectionProps) {
    return (
        <section className="ss-detail__section" aria-labelledby={id}>
            <h3 className="nv-section-title" id={id}>{title}</h3>
            {children}
        </section>
    );
}

/**
 * Detalle de un período del gráfico de evolución (un día, una semana o un mes), en un panel lateral de Novarium (420 px,
 * a todo el ancho en pantallas angostas): un resumen en palabras, indicadores contra el promedio del período, dónde se
 * esperó, a qué hora (o, en una semana o un mes, qué días) y las posibles causas. Usa los endpoints de siempre con las
 * fechas del período y los filtros de la pantalla. El título, las atenciones y la espera del punto se ven al instante; el
 * resto, con una carga por sección. Si no se puede consultar, una nota con «Reintentar». Esc cierra y el foco vuelve a
 * quien lo abrió.
 * Dentro del mismo panel se puede entrar a un día de una semana o un mes y a un servicio (tocando su fila en «Dónde se
 * esperó»): la vista de un servicio compara contra su propio habitual y tiene un enlace «« {período}» para volver.
 */
export default function PeriodDetailSidebar({
    target, filter, measure, overall, services, averageAttentions, scheduleAgendaId, mode = 'management',
    onViewRange, onHide, onAccessProblem,
}: PeriodDetailSidebarProps) {
    const intl = useIntl();
    const [navigation, setNavigation] = useState<Navigation | null>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const lastKey = useRef('');
    /** Mientras el panel se cierra se sigue mostrando lo último (sin parpadeo de «Cargando»). */
    const lastShown = useRef<{ view: PeriodView; parent: PeriodView | null } | null>(null);

    const root = useMemo<PeriodView | null>(() => (target ? { ...target, serviceId: 0, serviceName: '' } : null), [target]);
    const trail = navigation && navigation.owner === target ? navigation.trail : [];
    const current = root
        ? { view: trail[trail.length - 1] ?? root, parent: trail.length === 0 ? null : trail[trail.length - 2] ?? root }
        : null;
    if (current) lastShown.current = current;
    const shown = current ?? lastShown.current;

    const range = shown ? periodRange(shown.view.point, filter.initialDate, filter.finalDate) : null;
    const isDay = shown?.view.granularity === 'DAY';
    const serviceRow = shown && shown.view.serviceId ? services.find(item => item.serviceId === shown.view.serviceId) : undefined;
    /** Las agendas de consultorio tienen horario: la elegida en la pantalla o, mirando un servicio, ese servicio. */
    const agendaId = !shown || !isDay ? 0
        : shown.view.serviceId ? (serviceRow?.templateType === 1 ? shown.view.serviceId : 0) : scheduleAgendaId;
    // Cerrado, no se consulta nada (los filtros pueden cambiar con el panel cerrado)
    const request = current && range ? {
        filter: { ...periodFilter(filter, range.from, range.to), serviceId: current.view.serviceId || filter.serviceId },
        granularity: current.view.granularity,
        scheduleAgendaId: agendaId,
        skipServices: current.view.serviceId > 0,
    } : null;
    const detail = usePeriodDetail(request, onAccessProblem);

    const usualWait = useMemo(() => new Map(services.map(item => [item.serviceId, item.current.avgWaitMinutes])), [services]);
    const rankingRows = useMemo(
        () => scopeToService(detail.ranking.rows, shown?.view.serviceId || filter.serviceId || 0),
        [detail.ranking.rows, shown?.view.serviceId, filter.serviceId]);

    // Al pasar de una vista a otra el foco va al título: el lector de pantalla lo lee y el teclado no se queda en el vacío
    const viewKey = current ? `${current.view.serviceId}|${current.view.point.period}|${current.view.granularity}` : '';
    useEffect(() => {
        if (viewKey !== '' && lastKey.current !== '' && lastKey.current !== viewKey) titleRef.current?.focus();
        lastKey.current = viewKey;
    }, [viewKey]);

    if (!shown || !range || !root) return null;

    const { view, parent } = shown;
    const period = periodTitle(intl, view.point, view.granularity);
    const isService = view.serviceId > 0;
    const isClamped = view.granularity !== 'DAY'
        && (parseIsoDate(range.from).getTime() !== view.point.start.getTime()
            || parseIsoDate(range.to).getTime() !== view.point.end.getTime());
    const dayMonth = (iso: string) => intl.formatDate(parseIsoDate(iso), { day: '2-digit', month: '2-digit' });
    const core = detail.core;

    const go = (next: PeriodView) => { if (target) setNavigation({ owner: target, trail: [...trail, next] }); };
    const goBack = () => { if (target) setNavigation({ owner: target, trail: trail.slice(0, -1) }); };
    const selectService = (service: ServiceStatisticsServiceDTO) => go({
        point: {
            ...view.point,
            attentions: service.current.attentions,
            avgWait: service.current.avgWaitMinutes,
            avgService: service.current.avgServiceMinutes,
        },
        granularity: view.granularity,
        serviceId: service.serviceId,
        serviceName: service.serviceName,
    });
    const selectDay = (point: TimelinePoint) => go({
        point, granularity: 'DAY', serviceId: view.serviceId, serviceName: view.serviceName,
    });

    // Contra qué se compara: el promedio del período de la pantalla o, mirando un servicio, su propio habitual
    const rangeDays = countDays(parseIsoDate(filter.initialDate), parseIsoDate(filter.finalDate));
    const viewDays = countDays(parseIsoDate(range.from), parseIsoDate(range.to));
    const usual = serviceRow?.current;
    const reference: KpiReference = isService
        ? {
            kind: 'usual',
            attentions: usual ? (usual.attentions / rangeDays) * viewDays : 0,
            wait: usual ? usual.avgWaitMinutes : null,
            over60: usual && usual.attentions > 0 ? usual.waitOver60 / usual.attentions : null,
        }
        : {
            kind: 'period',
            attentions: view.granularity === 'DAY' ? averageAttentions.perDay : averageAttentions.perPeriod,
            wait: overall.avgWaitMinutes,
            over60: overall.attentions > 0 ? overall.waitOver60 / overall.attentions : null,
        };

    return (
        <Sidebar
            visible={target !== null}
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            blockScroll
            className="nv-scope nv-drawer ss-detail"
            aria-labelledby="ss-detail-title"
        >
            <div className="nv-drawer-layout">
                <header className="nv-drawer-head">
                    <div className="nv-stack-4">
                        {parent && (
                            <Button type="button" link icon="pi pi-angle-double-left"
                                label={periodTitle(intl, parent.point, parent.granularity)}
                                aria-label={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailBack' },
                                    { period: periodTitle(intl, parent.point, parent.granularity) })}
                                onClick={goBack} />
                        )}
                        <h2 className="nv-modal-title ss-detail__title" id="ss-detail-title" ref={titleRef} tabIndex={-1}>
                            {isService ? view.serviceName : period}
                        </h2>
                        {isService && <span className="nv-subtitle">{period}</span>}
                        {isClamped && (
                            <span>
                                <Tag className="nv-tag-neutral"
                                    value={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailClamped' },
                                        { from: dayMonth(range.from), to: dayMonth(range.to) })} />
                            </span>
                        )}
                    </div>
                    <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times"
                        aria-label={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailClose' })}
                        data-pr-tooltip={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailClose' })}
                        onClick={onHide} />
                </header>

                <div className="nv-drawer-body">
                    <PeriodDetailKpis point={view.point} granularity={view.granularity}
                        metrics={core.data?.summary.current ?? null} reference={reference} mode={mode} />

                    <Section id="ss-detail-longest-title"
                        title={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestTitle' })}>
                        <PeriodDetailLongestWaits rows={detail.longest.rows} status={detail.longest.status}
                            expanded={detail.longest.expanded} rangeFrom={range.from} rangeTo={range.to}
                            showService={!isService && !filter.serviceId}
                            onMore={detail.showMoreLongest} onRetry={detail.retryLongest} />
                    </Section>

                    {core.status === 'failed' ? (
                        <div className="ss-detail__failed" role="status">
                            <p className="nv-note">
                                <i className="pi pi-info-circle" aria-hidden="true" />
                                {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailFailed' }, { unit: view.granularity })}
                            </p>
                            <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })}
                                onClick={detail.retryCore} />
                        </div>
                    ) : (
                        <>
                            {!isService && (
                                <Section id="ss-detail-services-title"
                                    title={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailServicesTitle' })}>
                                    {core.data
                                        ? <PeriodDetailServices services={core.data.services} usualWait={usualWait}
                                            onSelect={selectService} />
                                        : <SectionLoading />}
                                </Section>
                            )}
                            <Section id="ss-detail-hours-title"
                                title={intl.formatMessage({ id: isDay ? 'ServicesStatisticsPeriodDetailHoursTitle' : 'ServicesStatisticsPeriodDetailDaysTitle' })}>
                                {!core.data ? <SectionLoading /> : isDay
                                    ? <PeriodDetailHours hourly={core.data.hourly} jornada={core.data.jornada} measure={measure} />
                                    : <PeriodDetailDays rows={core.data.days ?? []} from={range.from} to={range.to}
                                        onSelectDay={selectDay} />}
                            </Section>
                        </>
                    )}

                    <Section id="ss-detail-causes-title"
                        title={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailCausesTitle' })}>
                        <PeriodDetailCauses rows={rankingRows} status={detail.ranking.status} granularity={view.granularity}
                            single={isService || !!filter.serviceId} onRetry={detail.retryRanking} />
                    </Section>
                </div>

                <footer className="nv-drawer-foot">
                    <Button type="button" className="buttonMain3"
                        label={intl.formatMessage({ id: isService ? 'ServicesStatisticsPeriodDetailViewOnlyService' : 'ServicesStatisticsPeriodDetailViewOnly' },
                            { unit: view.granularity })}
                        onClick={() => onViewRange(range.from, range.to, view.serviceId)} />
                </footer>
            </div>
        </Sidebar>
    );
}
